import { t } from 'i18next';
import { Controller, Form, FormProvider } from 'react-hook-form';

import { useDetailsModal } from './hooks';
import {
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from '../../../../../../../components/DynamicFormFields';

import { theme } from '@store';
import {
  Box,
  Button,
  Switch,
  FormGroup,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import { CustomModal, SaveChangesModal } from '@components';
import { isNil } from 'lodash';
import { reportTypeOptions } from '@views/pages/Reporting/Reports/types.ts';

export const DetailsModal = ({ disabled }: { disabled: boolean }) => {
  const {
    formMethods,
    handleClose,
    isModalOpen,
    onSubmit,
    folderOptions,
    isSubmitting,
    isFormInitialized,
    isFolderPrivate,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEdit,
    isOwner,
  } = useDetailsModal();

  const { control, watch } = formMethods;

  const isPrivateDisabled = watch('allow_others_to_edit');
  const isAllowOthersToEditDisabled = watch('private');

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleClose}
      title={isFormInitialized ? 'Edit report' : 'Add a new report'}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <SelectFormField
            name='report_type'
            label='Type'
            options={reportTypeOptions}
            disabled={isEdit || disabled}
          />

          <TextFormField name='name' label='Name' disabled={disabled} />

          <SelectFormField
            name='report_folder_id'
            label='Folder'
            options={folderOptions}
            disabled={disabled}
          />

          <TextAreaFormField
            optional
            maxRows={4}
            disabled={disabled}
            name='description'
            label='Description'
            charLimit={400}
          />

          <Controller
            name='private'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    disabled={
                      isFolderPrivate ||
                      disabled ||
                      isPrivateDisabled ||
                      !isOwner
                    }
                    control={<Switch checked={field.value} {...field} />}
                    label={'Set as private'}
                  />
                </FormGroup>
                <FormHelperText error={!isNil(error)}>
                  {error ? error.message : null}
                </FormHelperText>
              </FormControl>
            )}
          />

          <Controller
            name='allow_others_to_edit'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <FormGroup>
                  <FormControlLabel
                    disabled={
                      isFolderPrivate ||
                      disabled ||
                      isAllowOthersToEditDisabled ||
                      !isOwner
                    }
                    control={<Switch checked={field.value} {...field} />}
                    label={'Allow others to edit'}
                  />
                </FormGroup>
                <FormHelperText error={!isNil(error)}>
                  {error ? error.message : null}
                </FormHelperText>
              </FormControl>
            )}
          />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              position: 'sticky',
              bottom: 0,
              paddingY: 3,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Button
              variant='text'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
              }}
              disabled={isSubmitting}
              onClick={handleClose}
            >
              {t('general.cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={isSubmitting || disabled || !isValid}
            >
              {t('general.save')}
            </Button>
          </Box>
          <SaveChangesModal
            isOpen={isSaveChangesModalOpen}
            onClose={handleCloseSaveChangesModal}
            isValid={isValid}
            handleSaveChanges={handleSaveChanges}
            discardChanges={handleDiscardChanges}
          />
        </Form>
      </FormProvider>
    </CustomModal>
  );
};
